import React, { useEffect, useState } from 'react';
import { documentTypes } from '../../../../../models/entities/shipment/shipment';
import { styled } from '../../../../shared/Theme/theme';
import { Form, Formik } from 'formik';
import Button from '../../../../shared/SmallComponents/Button';
import { ModalFooter, ModalFormContainer } from '../../../../shared/Modal/Modal';
import Loader from '../../../../shared/SmallComponents/Loader';
import { document } from '../../../../../models/entities/common_subentities/document';
import { FormDatePicker, FormDropDown, FormTextArea } from '../../../../shared/inputs/form';
import FieldLabel from '../../../../shared/SmallComponents/FieldLabel';
import UploadDnD from '../../../../shared/inputs/form/UploadDnD';

export enum formMode {
    NEW,
    EDIT
}

interface formFields {
    type?: string | string[];
    comment?: string;
    file?: File;
    expDate?: Date;
}
interface formFieldsErrors {
    type?: string | string[];
    comment?: string;
    expDate?: string;
    file?: string;
}
type Props<entityType, settingsType> = {
    entity?: entityType;
    document?: document;
    mode: formMode;
    localizations?: any;
    settings?: settingsType;
    openedFromModal?: {
        setVisible: React.Dispatch<React.SetStateAction<boolean>>;
    };
    confirmation?: { setConfirm: React.Dispatch<React.SetStateAction<boolean>> };
    createDocument: (entityId: string, document: document) => void;
    updateDocument: (entityId: string, document: document) => void;
    showLoader?: boolean;
    defaultValues?: document;
    typeMode?: 'multiple' | 'tags';
};

let AFTER_LOADING: boolean = false;
function DocumentFormConnected<entityType extends { id: string }, settingsType extends { documentTypes?: documentTypes } = any>({
    entity,
    document,
    localizations,
    mode,
    settings,
    openedFromModal,
    confirmation,
    createDocument,
    updateDocument,
    showLoader,
    defaultValues,
    typeMode
}: Props<entityType, settingsType>) {
    const [submitLoading, setLoading] = useState(false);
    const [file, setFile] = useState<File | undefined>(undefined);
    const documentTypesOptions = settings?.documentTypes?.map((item) => ({
        text: item.name,
        value: item.code
    }));
    let initFormValues: formFields = {
        type: '',
        comment: '',
        expDate: undefined,
        file: undefined
    };
    const setInitialValues = () => {
        if (!document || mode === formMode.NEW) {
            // For new documents, merge default values with initial values
            let typeValue = defaultValues?.type || '';
            
            // If typeMode is 'tags' and we have a string value, convert it to an array
            if (typeMode === 'tags' && typeof typeValue === 'string' && typeValue) {
                typeValue = [typeValue];
            }
            
            const initialValues = {
                type: typeValue,
                comment: defaultValues?.comment || '',
                expDate: defaultValues?.expDate || undefined,
                file: defaultValues?.file || undefined
            };
            return initialValues;
        } else {
            // For editing existing documents
            let typeValue = document.type || '';
            
            // If typeMode is 'tags' and we have a string value, convert it to an array
            if (typeMode === 'tags' && typeof typeValue === 'string' && typeValue) {
                typeValue = [typeValue];
            }
            
            return {
                type: typeValue,
                comment: document.comment || '',
                expDate: document.expDate || undefined,
                file: { name: document.fileName, type: document.fileType } as File
            };
        }
    };
    const validate = (values: any) => {
        if (confirmation) {
            if (JSON.stringify(values) === JSON.stringify(initFormValues)) {
                confirmation.setConfirm(false);
            } else {
                confirmation.setConfirm(true);
            }
        }
        const errors: formFieldsErrors = {};
        if (!values.type) errors.type = localizations.documents.form.required_error;
        if (!file) errors.file = localizations.documents.form.required_error;
        return errors;
    };
    const submit = (values: any) => {
        if (entity && file) {
            const payload: document = {
                type: values.type,
                comment: values.comment,
                fileName: file.name,
                fileType: file.type,
                file: file,
                expDate: values.expDate
            } as document;
            if (mode === formMode.NEW) createDocument(entity.id, payload);
            else if (document) {
                updateDocument(entity.id, {
                    ...document,
                    ...payload,
                    id: document.id
                });
            }
        }
    };
    useEffect(() => {
        if (showLoader) {
            setLoading(true);
            AFTER_LOADING = true;
        } else {
            setLoading(false);
            if (AFTER_LOADING && openedFromModal) {
                openedFromModal.setVisible(false);
                AFTER_LOADING = false;
            }
        }
    }, [showLoader, openedFromModal]);
    return (
        <Formik initialValues={setInitialValues()} validate={validate} onSubmit={submit}>
            <Form>
                <Container>
                    <FieldLabel>{localizations.documents.form.documenttype}</FieldLabel>
                    <FieldContainer>
                        <FormDropDown
                            minWidth="160px"
                            name="type"
                            placeHolder={localizations.documents.form.documenttype_placeholder}
                            options={documentTypesOptions || []}
                            mode={typeMode}
                        />
                    </FieldContainer>
                    <FieldLabel>{'Expiration Date'}</FieldLabel>
                    <FieldContainer>
                        <FormDatePicker name="expDate" placeHolder={'Expiration Date'} />
                    </FieldContainer>
                    <FieldLabel>{localizations.documents.form.comment}</FieldLabel>
                    <FieldContainer>
                        <FormTextArea name="comment" placeHolder={localizations.documents.form.comment_placeholder} />
                    </FieldContainer>
                    <FieldContainer>
                        <UploadDnD
                            multiple={false}
                            updateFiles={(files) => {
                                setFile(files as File);
                            }}
                        />
                        {file && (
                            <SelectedFileContainer>
                                Selected File: <span>{file.name}</span>
                            </SelectedFileContainer>
                        )}
                    </FieldContainer>
                </Container>
                {openedFromModal && (
                    <FormFooter>
                        <Button minWidth="55px" buttonType="filled" type="submit">
                            {submitLoading ? (
                                <Loader width="20px" marginTop="0px" showText={false} />
                            ) : (
                                <span>{localizations.documents.modal.submit_btn}</span>
                            )}
                        </Button>
                    </FormFooter>
                )}
            </Form>
        </Formik>
    );
}
const FieldContainer = styled.div`
    margin-bottom: 12px;
`;
const SelectedFileContainer = styled.div`
    display: flex;
    align-items: center;
    margin-top: 10px;
    width: 100%;
    text-align: center;
    span {
        text-align: center;
        font-weight: 800;
        margin-left: 10px;
    }
`;

const Container = styled(ModalFormContainer)`
    background: white;
`;

const FormFooter = styled(ModalFooter)`
    position: absolute;
    bottom: 0;
    width: 100%;
    left: 0;
    right: 0;
    background: white;
    justify-content: flex-end;
`;

export default DocumentFormConnected;
